/* eslint-disable no-underscore-dangle, react/prop-types */
import React, { useContext } from "react";
import PropTypes from "prop-types";
import { Button } from "reactstrap";
import moment from "moment";
import { Link } from "react-router-dom";
import DataTableControlled from "src/Components/DataTableControlled";
import __ from "src/utils/Translations";
import RbsContext from "src/utils/RoleBasedSecurity/RbsContext";
import { getAhrUrl } from "src/Pages/Report/helpers/ahrHelper";
import { GetCurrentCompany } from "src/Pages/Report/helpers/companyHelper";
import getUserConfirmationPopup from "src/utils/system/DOM/getUserConfirmationPopup";
import ActionButton from "src/Pages/Company/Absences/ActionButton/ActionButton";
import { REPORT_SERVICE, restApiRequest, getServiceHost } from "src/utils/Api";
import { getLanguage } from "src/utils/Languages/LanguageContext";
import { dynamicNotification } from "src/utils/Notifications";
import { getSession } from "src/utils/RoleBasedSecurity/Session";

const ReportsTable = ({ reportList = [], isAdHoc, refreshListCallback }) => {
  const rbsContext = useContext(RbsContext);
  const isAhr = rbsContext.userInfo.isAhr();
  const currentCompany = GetCurrentCompany();

  const getUrlToForm = (id, formMode) =>
    getAhrUrl(
      `/report/predefined-reports/${id}${
        !isAhr ? `/${currentCompany}` : ""
      }?mode=${formMode}`,
      isAhr
    );

  const columns = () => {
    const colArray = [];
    colArray.push({
      Header: __("Nazwa raportu"),
      accessor: "reportName",
      filterable: false,
      Cell: ({ row }) =>
        !row._original.underMaintenance ? (
          <div>{row.reportName}</div>
        ) : (
          <div> {row.reportName}</div>
        ),
    });
    if (isAdHoc) {
      colArray.push({
        Header: __("Utworzono"),
        maxWidth: 100,
        accessor: "reportInsertDate",
        filterable: false,
        Cell: ({ row }) => (
          <div
            style={{
              wordBreak: "normal",
              textAlign: "center",
            }}
            // eslint-disable-next-line
            dangerouslySetInnerHTML={{
              __html: moment(row.reportInsertDate).format(
                "YYYY-MM-DD HH:mm:ss"
              ),
            }}
            // 2023-03-20T11:18:11.17
          />
        ),
      });
    }
    if (isAdHoc) {
      colArray.push({
        Header: __("Utworzone przez"),
        maxWidth: 250,
        accessor: "createdBy",
        filterable: false,
        Cell: ({ row }) => (
          <div
            style={{
              wordBreak: "normal",
              textAlign: "center",
            }}
            // eslint-disable-next-line
            dangerouslySetInnerHTML={{ __html: row.createdBy }}
          />
        ),
      });
    }
    colArray.push({
      Header: __("Opis"),
      accessor: "description",
      filterable: false,
      Cell: ({ row }) => (
        <div
          style={{
            wordBreak: "normal",
            textAlign: "justify",
          }}
          // eslint-disable-next-line
          dangerouslySetInnerHTML={{ __html: row.description }}
        />
      ),
    });
    colArray.push({
      Header: __("Akcja"),
      maxWidth: 150,
      filterable: false,
      sortable: false,
      Cell: ({ row }) =>
        !row._original.underMaintenance ? (
          <>
            <div className="d-block w-100 text-center row">
              <div>
                <Link to={getUrlToForm(row._original.reportGuid, 1)}>
                  <Button
                    role="button"
                    color="link"
                    style={{ wordBreak: "break-word" }}
                  >
                    {__("Generuj raport")}
                  </Button>
                </Link>
              </div>
              <div>
                <Link to={getUrlToForm(row._original.reportGuid, 2)}>
                  <Button
                    role="button"
                    color="link"
                    style={{ wordBreak: "break-word" }}
                  >
                    {__("Ustaw wysyłkę cykliczną")}
                  </Button>
                </Link>
              </div>
              {row._original.userCanRemove ? (
                <Button
                  className="link-button"
                  color="link"
                  type="button"
                  onClick={() =>
                    getUserConfirmationPopup(
                      __(
                        "Jeżeli na danym raporcie jest ustawiona subskrypcja, zostanie ona automatycznie wyłączona. Czy chcesz usunąć raport personalizowany?"
                      ),
                      (confirm) => {
                        if (confirm === true) {
                          requestReportRemoval(
                            row._original.reportGuid,
                            currentCompany,
                            (confirmSuccess) => {
                              if (confirmSuccess && refreshListCallback) {
                                refreshListCallback();
                              }
                            }
                          );
                        }
                      },
                      "Pytanie",
                      0,
                      [],
                      "Tak, usuń"
                    )
                  }
                >
                  {__("Usuń")}
                </Button>
              ) : (
                <></>
              )}
            </div>
          </>
        ) : (
          <div className="d-block w-100 text-center row">
            {__("niedostępny")}
          </div>
        ),
    });
    return colArray;
  };

  return (
    <>
      <DataTableControlled
        id="reportsDTC"
        columns={columns()}
        data={reportList}
        filterable
        count={reportList.length}
        defaultPageSize={100}
        fetchData={() => {}}
      />
    </>
  );
};

const requestReportRemoval = async (reportId, companyId, callback) => {
  const session = getSession();
  const server = getServiceHost(REPORT_SERVICE);
  const url = `${process.env.REACT_APP_REPORTING_TOOL_URL}${server}/reports/deactivate`;

  const requestBody = JSON.stringify({
    reportId,
    companyId,
    languageCode: getLanguage(),
  });

  const response = await fetch(url, {
    method: "POST",
    headers: {
      accept: "application/json",
      "Content-Type": "application/json",
      "content-language": getLanguage(),
      Authorization: `Bearer ${await session.getValidAccessToken()}`,
      "Accept-Language": getLanguage(),
    },
    body: requestBody,
  });

  if (!response.ok) {
    const responseJson = await response.json();
    dynamicNotification(
      `${responseJson.title}: ${responseJson.detail}`,
      "error",
      "top-right",
      10000
    );
    callback(false);
  } else {
    dynamicNotification(
      __("Raport został usunięty"),
      "success",
      "top-right",
      10000
    );
    callback(true);
  }
};

ReportsTable.propTypes = {
  reportList: PropTypes.arrayOf(PropTypes.object).isRequired,
  isAdHoc: PropTypes.bool,
};

ReportsTable.defaultProps = {
  isAdHoc: false,
};

export default ReportsTable;
